import { TenantInfo } from './tenant-detection';

export interface BrandingConfig {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  surfaceColor?: string;
  textColor?: string;
  logo?: string;
  logoUrl?: string;
  favicon?: string;
  fontFamily?: string;
  borderRadius?: string;
  shadows?: boolean;
  animations?: boolean;
}

export interface ThemeVariables {
  '--cosmic-primary': string;
  '--cosmic-secondary': string;
  '--cosmic-accent': string;
  '--cosmic-background': string;
  '--cosmic-surface': string;
  '--cosmic-text': string;
  '--cosmic-text-muted': string;
  '--cosmic-border': string;
  '--cosmic-border-radius': string;
  '--cosmic-shadow': string;
  '--cosmic-font-family': string;
}

// Default COSMK theme
const DEFAULT_THEME: BrandingConfig = {
  primaryColor: '#7C3AED',
  secondaryColor: '#EC4899',
  accentColor: '#06B6D4',
  backgroundColor: '#0A0A0A',
  surfaceColor: '#1A1A1A',
  textColor: '#FFFFFF',
  borderRadius: '8px',
  shadows: true,
  animations: true,
  fontFamily: 'Inter, system-ui, sans-serif'
};

// Cache for loaded branding configurations
const brandingCache = new Map<string, { config: BrandingConfig; timestamp: number }>();
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

/**
 * Generate CSS variables from branding configuration
 */
export function generateThemeVariables(branding: BrandingConfig): ThemeVariables {
  const config = { ...DEFAULT_THEME, ...branding };
  
  return {
    '--cosmic-primary': config.primaryColor!,
    '--cosmic-secondary': config.secondaryColor!,
    '--cosmic-accent': config.accentColor!,
    '--cosmic-background': config.backgroundColor!,
    '--cosmic-surface': config.surfaceColor!,
    '--cosmic-text': config.textColor!,
    '--cosmic-text-muted': adjustColorOpacity(config.textColor!, 0.7),
    '--cosmic-border': adjustColorOpacity(config.primaryColor!, 0.2),
    '--cosmic-border-radius': config.borderRadius!,
    '--cosmic-shadow': config.shadows ? '0 10px 25px -5px rgba(0, 0, 0, 0.25)' : 'none',
    '--cosmic-font-family': config.fontFamily!
  };
}

/**
 * Apply tenant branding to the document
 */
export function applyTenantBranding(tenant: TenantInfo | null): void {
  if (!tenant?.branding_config) {
    // Apply default theme
    applyThemeVariables(generateThemeVariables(DEFAULT_THEME));
    updateFavicon();
    updateDocumentTitle();
    return;
  }

  const branding = tenant.branding_config as BrandingConfig;
  
  // Apply CSS variables
  applyThemeVariables(generateThemeVariables(branding));
  
  // Update favicon
  if (branding.favicon) {
    updateFavicon(branding.favicon);
  }
  
  // Update document title
  updateDocumentTitle(tenant.name);
  
  // Apply logo
  if (branding.logo || branding.logoUrl) {
    updateLogo(branding.logo || branding.logoUrl!);
  }
  
  // Apply custom fonts
  if (branding.fontFamily && branding.fontFamily !== DEFAULT_THEME.fontFamily) {
    loadCustomFont(branding.fontFamily);
  }
  
  // Handle animations
  if (branding.animations === false) {
    disableAnimations();
  } else {
    enableAnimations();
  }
}

/**
 * Apply theme variables to document root
 */
function applyThemeVariables(variables: ThemeVariables): void {
  const root = document.documentElement;
  
  Object.entries(variables).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
}

/**
 * Update document favicon
 */
function updateFavicon(faviconUrl?: string): void {
  const defaultFavicon = '/favicon.ico';
  const url = faviconUrl || defaultFavicon;
  
  // Remove existing favicon links
  const existingLinks = document.querySelectorAll('link[rel*="icon"]');
  existingLinks.forEach(link => link.remove());
  
  // Add new favicon
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/x-icon';
  link.href = url;
  document.head.appendChild(link);
  
  // Add apple touch icon
  const appleLink = document.createElement('link');
  appleLink.rel = 'apple-touch-icon';
  appleLink.href = url;
  document.head.appendChild(appleLink);
}

/**
 * Update document title
 */
function updateDocumentTitle(tenantName?: string): void {
  const defaultTitle = 'COSMK DRM Platform';
  document.title = tenantName ? `${tenantName} | COSMK` : defaultTitle;
}

/**
 * Update logo elements throughout the application
 */
function updateLogo(logoUrl: string): void {
  // Update all elements with data-tenant-logo attribute
  const logoElements = document.querySelectorAll('[data-tenant-logo]');
  logoElements.forEach(element => {
    if (element instanceof HTMLImageElement) {
      element.src = logoUrl;
    } else if (element instanceof HTMLElement) {
      element.style.backgroundImage = `url(${logoUrl})`;
    }
  });
  
  // Dispatch event for components to update
  window.dispatchEvent(new CustomEvent('tenant-logo-updated', { 
    detail: { logoUrl } 
  }));
}

/**
 * Load custom font family
 */
function loadCustomFont(fontFamily: string): void {
  // Check if font is already loaded
  if (document.fonts.check(`12px "${fontFamily}"`)) {
    return;
  }
  
  // Try to load from Google Fonts
  const googleFontsUrl = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
  
  // Check if link already exists
  const existingLink = document.querySelector(`link[href*="${fontFamily}"]`);
  if (existingLink) return;
  
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = googleFontsUrl;
  document.head.appendChild(link);
}

/**
 * Disable animations for performance or accessibility
 */
function disableAnimations(): void {
  const style = document.createElement('style');
  style.id = 'tenant-animation-override';
  style.textContent = `
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  `;
  document.head.appendChild(style);
}

/**
 * Enable animations (remove override)
 */
function enableAnimations(): void {
  const existingStyle = document.getElementById('tenant-animation-override');
  if (existingStyle) {
    existingStyle.remove();
  }
}

/**
 * Adjust color opacity
 */
function adjustColorOpacity(color: string, opacity: number): string {
  // Handle hex colors
  if (color.startsWith('#')) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  
  // Handle rgb/rgba colors
  if (color.startsWith('rgb')) {
    const rgbMatch = color.match(/\d+/g);
    if (rgbMatch && rgbMatch.length >= 3) {
      return `rgba(${rgbMatch[0]}, ${rgbMatch[1]}, ${rgbMatch[2]}, ${opacity})`;
    }
  }
  
  // Fallback
  return color;
}

/**
 * Generate complementary colors based on primary color
 */
export function generateColorPalette(primaryColor: string): {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
} {
  // Convert hex to HSL for better color manipulation
  const hsl = hexToHsl(primaryColor);
  
  return {
    primary: primaryColor,
    secondary: hslToHex((hsl.h + 60) % 360, hsl.s, hsl.l), // Complementary hue
    accent: hslToHex((hsl.h + 180) % 360, hsl.s, Math.min(hsl.l + 20, 100)),
    background: '#0A0A0A',
    surface: '#1A1A1A',
    text: '#FFFFFF'
  };
}

/**
 * Convert hex color to HSL
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Convert HSL to hex color
 */
function hslToHex(h: number, s: number, l: number): string {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  
  const r = Math.round(hue2rgb(p, q, h + 1/3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1/3) * 255);

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Validate color format
 */
export function isValidColor(color: string): boolean {
  // Test hex colors
  if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
    return true;
  }
  
  // Test rgb/rgba colors
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+)?\s*\)$/.test(color)) {
    return true;
  }
  
  // Test named colors (basic set)
  const namedColors = ['red', 'green', 'blue', 'white', 'black', 'yellow', 'purple', 'orange', 'pink', 'gray'];
  return namedColors.includes(color.toLowerCase());
}

/**
 * Get contrast ratio between two colors
 */
export function getContrastRatio(color1: string, color2: string): number {
  const getLuminance = (color: string): number => {
    // Simplified luminance calculation
    const rgb = color.startsWith('#') 
      ? [
          parseInt(color.slice(1, 3), 16),
          parseInt(color.slice(3, 5), 16),
          parseInt(color.slice(5, 7), 16)
        ]
      : [0, 0, 0]; // Fallback
    
    const [r, g, b] = rgb.map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  
  return (brightest + 0.05) / (darkest + 0.05);
}

/**
 * Check if color combination meets accessibility standards
 */
export function isAccessibleContrast(foreground: string, background: string): boolean {
  const ratio = getContrastRatio(foreground, background);
  return ratio >= 4.5; // WCAG AA standard for normal text
}

/**
 * Reset branding to default
 */
export function resetToDefaultBranding(): void {
  applyTenantBranding(null);
}

/**
 * Get current applied branding configuration
 */
export function getCurrentBranding(): BrandingConfig {
  const root = document.documentElement;
  const computedStyle = getComputedStyle(root);
  
  return {
    primaryColor: computedStyle.getPropertyValue('--cosmic-primary').trim(),
    secondaryColor: computedStyle.getPropertyValue('--cosmic-secondary').trim(),
    accentColor: computedStyle.getPropertyValue('--cosmic-accent').trim(),
    backgroundColor: computedStyle.getPropertyValue('--cosmic-background').trim(),
    surfaceColor: computedStyle.getPropertyValue('--cosmic-surface').trim(),
    textColor: computedStyle.getPropertyValue('--cosmic-text').trim(),
    borderRadius: computedStyle.getPropertyValue('--cosmic-border-radius').trim(),
    fontFamily: computedStyle.getPropertyValue('--cosmic-font-family').trim()
  };
}

/**
 * Export branding configuration
 */
export function exportBrandingConfig(tenant: TenantInfo): string {
  const config = {
    tenantName: tenant.name,
    branding: tenant.branding_config,
    exportedAt: new Date().toISOString()
  };
  
  return JSON.stringify(config, null, 2);
}

/**
 * Import and validate branding configuration
 */
export function importBrandingConfig(jsonString: string): BrandingConfig {
  try {
    const config = JSON.parse(jsonString);
    
    // Validate required fields
    const validatedConfig: BrandingConfig = {};
    
    if (config.branding?.primaryColor && isValidColor(config.branding.primaryColor)) {
      validatedConfig.primaryColor = config.branding.primaryColor;
    }
    
    if (config.branding?.secondaryColor && isValidColor(config.branding.secondaryColor)) {
      validatedConfig.secondaryColor = config.branding.secondaryColor;
    }
    
    // Add other validation as needed
    
    return validatedConfig;
  } catch (error) {
    throw new Error('Invalid branding configuration format');
  }
}